import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Flashcard, FlashcardsState } from '../../types/Flashcard';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../store/store';
import { cacheContentImages } from '../../utils/imgCaching';
import moment from 'moment';
import {
  normalizeFlashcard,
  normalizeFlashcardsState,
} from '../../utils/flashcardVariants';
import {
  dueOrderKey,
  localStartOfTomorrow,
} from '../../utils/flashcardOrderJitter';

const initialState: FlashcardsState = {
  new: [],
  due: [],
  completed: [],
  syncToTodaySession: false,
  todaySessionCompletedBaseCount: 0,
  todaySessionTotalCount: 0,
};

const cacheFirstTwoFlashcards = (state: FlashcardsState) => {
  state.new.slice(0, 2).forEach((flashcard) => {
    cacheContentImages(flashcard);
  });

  state.due.slice(0, 2).forEach((flashcard) => {
    cacheContentImages(flashcard);
  });
};

const flashcardsSlice = createSlice({
  name: 'flashcards',
  initialState,
  reducers: {
    // A listener (src/middleware/flashcardCountSyncListener.ts) persists the
    // completed count of today's session to the backend after flashcardsLoaded,
    // flashcardUpdated, flashcardSuspended and flashcardRemoved.
    flashcardsLoaded: (state, action: PayloadAction<FlashcardsState>) => {
      const normalized = normalizeFlashcardsState(action.payload);
      state.new = normalized.new;
      state.due = normalized.due;
      state.completed = normalized.completed;
      state.syncToTodaySession = Boolean(action.payload.syncToTodaySession);

      state.todaySessionCompletedBaseCount = state.syncToTodaySession
        ? Math.max(0, action.payload.todaySessionCompletedBaseCount ?? 0)
        : 0;

      state.todaySessionTotalCount = state.syncToTodaySession
        ? Math.max(0, action.payload.todaySessionTotalCount ?? 0)
        : 0;

      // Jittered due order: most-overdue first overall, without replaying the
      // exact same sequence every session
      const tomorrow = localStartOfTomorrow();

      state.due.sort(
        (a, b) =>
          flashcardDueOrderKey(a, tomorrow) - flashcardDueOrderKey(b, tomorrow)
      );

      // Cache images for first two flashcards
      cacheFirstTwoFlashcards(state);
    },
    flashcardUpdated: (state, action: PayloadAction<Flashcard>) => {
      const flashcard = normalizeFlashcard(action.payload);

      // Remove from all arrays
      state.new = state.new.filter((f) => f.id !== flashcard.id);
      state.due = state.due.filter((f) => f.id !== flashcard.id);
      state.completed = state.completed.filter((f) => f.id !== flashcard.id);

      const startOfTomorrow = moment().add(1, 'day').startOf('day');

      // Add to appropriate array
      if (
        flashcard.scheduling.state === 0 &&
        !flashcard.scheduling.isSuspended
      ) {
        state.new.push(flashcard);
      } else if (
        flashcard.scheduling.state !== 0 &&
        !flashcard.scheduling.isSuspended &&
        moment(flashcard.scheduling.due).isBefore(startOfTomorrow)
      ) {
        insertSorted(state.due, flashcard);
      } else {
        state.completed.push(flashcard);
      }

      // Cache images for first two flashcards after update
      cacheFirstTwoFlashcards(state);
    },
    flashcardSuspended: (state, action: PayloadAction<Flashcard>) => {
      const flashcard = normalizeFlashcard(action.payload);
      const wasInNew = state.new.some((f) => f.id === flashcard.id);
      const wasInDue = state.due.some((f) => f.id === flashcard.id);
      const wasInCompleted = state.completed.some((f) => f.id === flashcard.id);

      state.new = state.new.filter((f) => f.id !== flashcard.id);
      state.due = state.due.filter((f) => f.id !== flashcard.id);

      if (!wasInCompleted) {
        state.completed.push(flashcard);
      }

      // Cache images since we removed a flashcard that might have been in first two
      // or changed completed composition.
      if (wasInNew || wasInDue || !wasInCompleted) {
        cacheFirstTwoFlashcards(state);
      }
    },
    flashcardRemoved: (state, action: PayloadAction<Flashcard>) => {
      const flashcard = normalizeFlashcard(action.payload);
      state.new = state.new.filter((f) => f.id !== flashcard.id);
      state.due = state.due.filter((f) => f.id !== flashcard.id);
      state.completed = state.completed.filter((f) => f.id !== flashcard.id);

      // Cache images since we removed a flashcard that might have been in first two
      cacheFirstTwoFlashcards(state);
    },
    unshiftNew: (state, action: PayloadAction<Flashcard>) => {
      state.new.unshift(normalizeFlashcard(action.payload));

      // Cache images since we added to the beginning, changing first two
      cacheFirstTwoFlashcards(state);
    },
    unshiftDue: (state, action: PayloadAction<Flashcard>) => {
      state.due.unshift(normalizeFlashcard(action.payload));

      // Remove duplicates from due flashcards by ID
      state.due = state.due.filter(
        (dueFlashcard, index, self) =>
          index === self.findIndex((t) => t.id === dueFlashcard.id)
      );

      // Cache images since we added to the beginning, changing first two
      cacheFirstTwoFlashcards(state);
    },
    clearFlashcards: () => {
      return initialState;
    },
  },
});

const flashcardDueOrderKey = (flashcard: Flashcard, tomorrow: Date) =>
  dueOrderKey({
    cardId: flashcard.id,
    due: flashcard.scheduling.due,
    lastReview: flashcard.scheduling.lastReview,
    tomorrow,
  });

const insertSorted = (array: Flashcard[], item: Flashcard) => {
  const tomorrow = localStartOfTomorrow();
  const itemKey = flashcardDueOrderKey(item, tomorrow);
  let low = 0;
  let high = array.length;

  while (low < high) {
    const mid = (low + high) >>> 1;
    if (flashcardDueOrderKey(array[mid], tomorrow) < itemKey) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  array.splice(low, 0, item);
};

export const selectCompletedFlashcardsCount = createSelector(
  [(state: RootState) => state.flashcards],
  (flashcards) => {
    return flashcards.completed?.length || 0;
  }
);

export const selectCompletedFlashcardsCountForSession = createSelector(
  [
    (state: RootState) => state.flashcards,
    (state: RootState) => state.todaySession.session,
  ],
  (flashcards, todaySession) => {
    if (flashcards.syncToTodaySession) {
      return (
        (flashcards.todaySessionCompletedBaseCount ?? 0) +
        (flashcards.completed?.length || 0)
      );
    }
    return todaySession?.completedFlashcardCount || 0;
  }
);

export const selectTotalFlashcardsCount = createSelector(
  [(state: RootState) => state.flashcards],
  (flashcards) => {
    return (
      flashcards.new?.length +
      flashcards.due?.length +
      flashcards.completed?.length
    );
  }
);

export const selectTotalFlashcardsCountForSession = createSelector(
  [
    (state: RootState) => state.flashcards,
    (state: RootState) => state.todaySession.session,
  ],
  (flashcards, todaySession) => {
    if (flashcards.syncToTodaySession) {
      return (
        flashcards.todaySessionTotalCount || todaySession?.flashcardCount || 0
      );
    }
    return todaySession?.flashcardCount || 0;
  }
);

export const {
  flashcardsLoaded,
  flashcardUpdated,
  flashcardSuspended,
  flashcardRemoved,
  unshiftNew,
  unshiftDue,
  clearFlashcards,
} = flashcardsSlice.actions;

export default flashcardsSlice.reducer;
